
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import { isAuthenticated } from '../utils/auth';
import api from '../services/api';
import './Conversations.css';

interface Transcription {
  id: string;
  transcription_text: string;
  recording_duration: number;
  created_at: string;
}

interface Call {
  id: string;
  call_sid: string;
  direction: string;
  from_number: string;
  to_number: string;
  call_status: string;
  created_at: string;
  transcriptions: Transcription[];
}

interface Conversation {
  id: string;
  from_number: string;
  to_number: string;
  number_1: string;
  number_2: string;
  created_at: string;
  calls: Call[];
}

const Conversations: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Conversations');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchConversations();
  }, [navigate]);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/conversations');
      console.log('Conversations API Response:', response.data);
      
      if (response.data.success) {
        setConversations(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load conversations');
      }
    } catch (err: any) {
      console.error('Error fetching conversations:', err);
      setError(err.response?.data?.message || 'Failed to load conversations data');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`/${tab.toLowerCase()}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const getStatusColor = (status: string) => {
    switch(status?.toLowerCase()) {
      case 'completed': return '#2ecc71';
      case 'in-progress': return '#f39c12';
      case 'failed': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  if (!isAuthenticated()) {
    return null;
  }

  if (loading) {
    return (
      <div className="conversations-page">
        <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="page-content">
          <div className="loading-container">Loading conversations...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="conversations-page">
      <Navbar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="page-content">
        <h1>Conversations</h1>
        {error && <div className="error-message">{error}</div>}
        
        {conversations.length === 0 && !error ? (
          <div className="no-data">No conversations found</div>
        ) : (
          <div className="conversations-list">
            {conversations.map((conversation) => (
              <div key={conversation.id} className="conversation-card">
                <div className="conversation-header" onClick={() => toggleExpand(conversation.id)}>
                  <div className="conversation-info">
                    <div className="participants">
                      <span className="from-number">{conversation.from_number}</span>
                      <span className="arrow">→</span>
                      <span className="to-number">{conversation.to_number}</span>
                    </div>
                    <div className="conversation-meta">
                      <span className="date">{formatDate(conversation.created_at)}</span>
                      <span className="call-count">{conversation.calls?.length || 0} call(s)</span>
                    </div>
                  </div>
                  <div className="expand-icon">
                    {expandedId === conversation.id ? '▲' : '▼'}
                  </div>
                </div>
                
                {expandedId === conversation.id && (
                  <div className="conversation-details">
                    <div className="details-section">
                      <h4>Conversation Details</h4>
                      <div className="detail-row">
                        <strong>Conversation ID:</strong> {conversation.id}
                      </div>
                      <div className="detail-row">
                        <strong>From:</strong> {conversation.from_number}
                      </div>
                      <div className="detail-row">
                        <strong>To:</strong> {conversation.to_number}
                      </div>
                      <div className="detail-row">
                        <strong>Created:</strong> {formatDate(conversation.created_at)}
                      </div>
                    </div>
                    
                    {conversation.calls && conversation.calls.length > 0 && (
                      <div className="calls-section">
                        <h4>Calls in this Conversation</h4>
                        {conversation.calls.map((call) => (
                          <div key={call.id} className="call-item">
                            <div className="call-header">
                              <span className="call-direction">
                                {call.direction === 'inbound' ? '📞 Inbound' : '📤 Outbound'}
                              </span>
                              <span className="call-status" style={{ background: getStatusColor(call.call_status) }}>
                                {call.call_status}
                              </span>
                            </div>
                            <div className="call-details">
                              <div><strong>Call SID:</strong> {call.call_sid}</div>
                              <div><strong>From:</strong> {call.from_number}</div>
                              <div><strong>To:</strong> {call.to_number}</div>
                              <div><strong>Time:</strong> {formatDate(call.created_at)}</div>
                            </div>
                            
                            {call.transcriptions && call.transcriptions.length > 0 && (
                              <div className="transcription-section">
                                <h5>Transcription</h5>
                                <div className="transcription-text">
                                  {call.transcriptions[0].transcription_text}
                                </div>
                                <div className="transcription-meta">
                                  <span>Duration: {call.transcriptions[0].recording_duration} seconds</span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Conversations;